type SentryModule = typeof import("@sentry/nextjs");
type SentryEvent = Parameters<
  NonNullable<NonNullable<Parameters<SentryModule["init"]>[0]>["beforeSend"]>
>[0];

const isBrowser = typeof window !== "undefined";
const dsn = isBrowser ? process.env.NEXT_PUBLIC_SENTRY_DSN : process.env.SENTRY_DSN;
const enabled = process.env.NODE_ENV === "production" && Boolean(dsn);

let sentryPromise: Promise<SentryModule> | null = null;
let initialized = false;
let initializationScheduled = false;
let removeEarlyErrorListeners: (() => void) | null = null;

const EXECUTOR_M_ID_ERROR = /^Cannot read properties of undefined \(reading 'M_ID'\)$/;
const EXECUTOR_SCRIPT = "app:///executors/200.js";
const TYPEWRITER_REMOVE_CHILD_ERROR = /^Cannot read properties of null \(reading 'removeChild'\)$/;
const TYPEWRITER_SOURCE = "webpack://Typewriter/./src/core/Typewriter.js";

const eventMessages = (event: SentryEvent): string[] => {
  const messages = [event.message, event.logentry?.message];

  event.exception?.values?.forEach((exception) => {
    messages.push(exception.value);
    if (exception.type && exception.value) {
      messages.push(`${exception.type}: ${exception.value}`);
    }
  });

  return messages.filter((message): message is string => Boolean(message));
};

const hasOnlyExecutorFrames = (event: SentryEvent): boolean => {
  const frames = event.exception?.values?.flatMap(
    (exception) => exception.stacktrace?.frames || []
  );
  if (!frames?.length) return false;

  return frames.every((frame) => (frame.filename || frame.abs_path) === EXECUTOR_SCRIPT);
};

const hasTypewriterFrame = (event: SentryEvent): boolean =>
  event.exception?.values?.some((exception) =>
    exception.stacktrace?.frames?.some(
      (frame) => (frame.filename || frame.abs_path) === TYPEWRITER_SOURCE
    )
  ) === true;

const shouldIgnoreEvent = (event: SentryEvent): boolean => {
  const messages = eventMessages(event);

  const isExecutorNoise =
    messages.some((message) => EXECUTOR_M_ID_ERROR.test(message)) && hasOnlyExecutorFrames(event);
  const isTypewriterNoise =
    messages.some((message) => TYPEWRITER_REMOVE_CHILD_ERROR.test(message)) &&
    hasTypewriterFrame(event);

  return isExecutorNoise || isTypewriterNoise;
};

const initializeSentry = async (): Promise<SentryModule> => {
  sentryPromise ??= import("@sentry/nextjs");
  const Sentry = await sentryPromise;

  if (!initialized && enabled && isBrowser) {
    removeEarlyErrorListeners?.();
    removeEarlyErrorListeners = null;

    Sentry.init({
      dsn,
      enabled,
      environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
      tracesSampleRate: 0,
      ignoreErrors: [
        "NetworkError",
        "OneSignalSDK",
        "AbortError",
        "push notification",
        /^Timeout$/,
        "Failed to fetch",
        "Load failed",
      ],
      integrations: [Sentry.captureConsoleIntegration({ levels: ["error"] })],
      debug: false,
      beforeSend(event) {
        if (shouldIgnoreEvent(event)) return null;

        const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
        const message = event.exception?.values?.[0]?.value || "";
        const isNetworkError =
          message === "Failed to fetch" || message === "Load failed" || message === "NetworkError";

        return isOffline && isNetworkError ? null : event;
      },
    });

    initialized = true;
  }

  return Sentry;
};

const withSentry = (callback: (Sentry: SentryModule) => void) => {
  if (!enabled) return;
  void initializeSentry().then(callback);
};

export const captureException = (...args: Parameters<SentryModule["captureException"]>): void => {
  withSentry((Sentry) => Sentry.captureException(...args));
};

export const captureMessage = (...args: Parameters<SentryModule["captureMessage"]>): void => {
  withSentry((Sentry) => Sentry.captureMessage(...args));
};

export const addBreadcrumb = (...args: Parameters<SentryModule["addBreadcrumb"]>): void => {
  withSentry((Sentry) => Sentry.addBreadcrumb(...args));
};

export const captureRouterTransitionStart = (
  ...args: Parameters<SentryModule["captureRouterTransitionStart"]>
): void => {
  // Navigation tracing is disabled, so do not pull the SDK into the critical
  // path just for this hook. Forward transitions once initialization has begun.
  if (!sentryPromise) return;
  void sentryPromise.then((Sentry) => Sentry.captureRouterTransitionStart(...args));
};

const installEarlyErrorListeners = () => {
  if (typeof window === "undefined" || removeEarlyErrorListeners) return;

  const handleError = (event: ErrorEvent) => {
    captureException(event.error || new Error(event.message));
  };
  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    captureException(
      event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    );
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  removeEarlyErrorListeners = () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  };
};

export const scheduleSentryInitialization = (): void => {
  if (!enabled || typeof window === "undefined" || initializationScheduled) return;
  initializationScheduled = true;
  installEarlyErrorListeners();
};
